import type {
  ProductPageKey,
  ProductPagePayload,
} from "../../shared/contracts/product-pages.js";
import { ProductPageRequestError } from "./product-page-error.js";
import type { ProfileUpdateInput } from "../../shared/contracts/profile.js";

export async function loadProductPage(
  page: ProductPageKey,
  options: { rivalAccountId?: string; year?: number } = {},
): Promise<ProductPagePayload> {
  const params = new URLSearchParams();
  if (options.rivalAccountId)
    params.set("rivalAccountId", options.rivalAccountId);
  if (options.year !== undefined) params.set("year", String(options.year));
  const query = params.size ? `?${params}` : "";
  const response = await fetch(`/api/v1/pages/${page}${query}`, {
    credentials: "include",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new ProductPageRequestError(response.status);
  return response.json();
}

export async function updateProfile(input: ProfileUpdateInput): Promise<void> {
  await requestJson("/api/v1/profile", "PATCH", { ...input });
}

export async function uploadAvatar(file: File): Promise<void> {
  const response = await fetch("/api/v1/profile/avatar", {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": file.type },
    body: file,
  });
  if (!response.ok) throw new Error(await problemMessage(response));
}

export async function downloadAccountExport(): Promise<void> {
  const response = await fetch("/api/v1/account/export", {
    credentials: "include",
    headers: { accept: "application/json" },
  });
  if (!response.ok) throw new Error(await problemMessage(response));
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "fairway-rivalry-export.json";
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function deleteAccount(password: string): Promise<void> {
  await requestJson("/api/v1/account/delete", "POST", {
    password,
    confirmation: "DELETE",
  });
}

async function requestJson(
  path: string,
  method: "PATCH" | "POST",
  body: Record<string, unknown>,
): Promise<void> {
  const response = await fetch(path, {
    method,
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(await problemMessage(response));
}

async function problemMessage(response: Response): Promise<string> {
  const body = (await response.json().catch(() => null)) as {
    detail?: unknown;
  } | null;
  return typeof body?.detail === "string"
    ? body.detail
    : "The request could not be completed.";
}
